import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { useCompanyScopeContext } from "@ryde/redux";
import {
  BOOKING_POLLING_INTERVAL_MS,
  bookingsPollingRunningCountAtom,
  bookingsPollingStateAtom,
  createCatchErrorResult,
  createSuccessResultVoid,
  currentBulkSetBookingsTokenAtom,
  defaultBookingsPollingState,
  GlobalAppEventEnum,
  Log,
  simpleResultInvalidExecutionResult,
  warnAndToastSimpleResult,
  type IAbortBookingPollingEventArgs,
  type IBookingsPollingInitResult,
  type IBulkSetBookingsDto,
  type IBulkUpdateBookingsDateTimeDto,
  type SimpleResult,
} from "@ryde/shared";

import {
  createBookingsPollingStream,
  isBookingsPollingCurrentStateNotRunning,
  isBookingsPollingStatusRunning,
} from "./bookings-polling.helpers";
import { useBookingsPollingActions } from "./use-bookings-polling.actions";

export const useBookingsPolling = () => {
  const { eventEmitter } = useCompanyScopeContext();
  const { t } = useTranslation();
  const [bookingsPollingState, setBookingsPollingState] = useRecoilState(
    bookingsPollingStateAtom,
  );
  const [currentToken, setCurrentToken] = useRecoilState(
    currentBulkSetBookingsTokenAtom,
  );
  const {
    bookingsPollingStart,
    bookingsDateTimeUpdatePollingStart,
    backgroundCheckBookingsPolling,
    subscriptionRef,
    stopAndCleanupBookingsPolling,
    apiAbortBookingsPollingIfRunning,
    apiCheckBookingsPollingState,
  } = useBookingsPollingActions();

  const setPollingCount = useSetRecoilState(bookingsPollingRunningCountAtom);

  // The complement of the checker's own stop decision, so the two cannot disagree. Notably
  // ConflictError is not a "running" status but is transient - the backend keeps going and reports
  // InProgress again - so stopping the stream on it would strand the UI mid-run.
  const isPollingRunning = useEvent(
    () => !isBookingsPollingCurrentStateNotRunning(bookingsPollingState),
  );

  const initBookingsPolling = useEvent(
    (initData: IBookingsPollingInitResult): SimpleResult<void> => {
      try {
        Log.debug("Init bookings polling", bookingsPollingState);
        setBookingsPollingState({ ...initData.state, token: initData.token });
        setCurrentToken(initData.token);

        if (subscriptionRef?.current && !subscriptionRef.current.closed) {
          Log.debug("Existing polling found, unsubscribing before init");
          subscriptionRef.current.unsubscribe();
        }

        subscriptionRef.current = createBookingsPollingStream({
          intervalMs: BOOKING_POLLING_INTERVAL_MS,
          isRunning: isPollingRunning,
          onTick: pollingCount => {
            Log.debug(`Bookings create, polling: ${pollingCount}`);
            setPollingCount(pollingCount);
            backgroundCheckBookingsPolling();
          },
        }).subscribe();
        return createSuccessResultVoid();
      } catch (error) {
        stopAndCleanupBookingsPolling();
        return createCatchErrorResult(error, t);
      }
    },
  );

  /**
   * Shared by every run that drives this stream: it clears a stale run, resets the state the panel
   * reads, then subscribes on whatever the starter returns. Only the init call differs per flow.
   */
  const startBookingsPollingRun = useEvent(
    async (
      startRun: () => Promise<SimpleResult<IBookingsPollingInitResult>>,
    ): Promise<void> => {
      if (isBookingsPollingStatusRunning(bookingsPollingState)) {
        if (bookingsPollingState?.token !== currentToken) {
          Log.info(
            "Stopping previous polling",
            bookingsPollingState?.token,
            currentToken,
          );
          stopAndCleanupBookingsPolling();
        } else {
          Log.info("Polling with current token is already running");
          return;
        }
      }

      setBookingsPollingState(defaultBookingsPollingState);

      const result = await startRun();

      if (!result.success) {
        warnAndToastSimpleResult(result, t);
        return;
      }

      if (!result.data) {
        warnAndToastSimpleResult(simpleResultInvalidExecutionResult(t), t);
        return;
      }

      Log.debug("Booking polling started", result.data);
      initBookingsPolling(result.data);
    },
  );

  const onInitBookingPolling = useEvent(
    async (payload: IBulkSetBookingsDto): Promise<void> => {
      Log.debug("Handling  bookings polling init event", payload);
      await startBookingsPollingRun(() => bookingsPollingStart(payload));
    },
  );

  const onInitBookingsDateTimeUpdatePolling = useEvent(
    async (payload: IBulkUpdateBookingsDateTimeDto): Promise<void> => {
      Log.debug("Handling bookings date and time update init event", payload);
      await startBookingsPollingRun(() =>
        bookingsDateTimeUpdatePollingStart(payload),
      );
    },
  );

  const onAbortBookingPolling = useEvent(
    ({ token }: IAbortBookingPollingEventArgs) => {
      Log.debug("Booking polling aborted", token);
      apiAbortBookingsPollingIfRunning();
    },
  );
  useEffect(() => {
    eventEmitter?.on(
      GlobalAppEventEnum.INIT_BOOKINGS_POLLING,
      onInitBookingPolling,
    );
    eventEmitter?.on(
      GlobalAppEventEnum.INIT_BOOKINGS_DATE_TIME_UPDATE_POLLING,
      onInitBookingsDateTimeUpdatePolling,
    );
    eventEmitter?.on(
      GlobalAppEventEnum.ABORT_BOOKINGS_POLLING,
      onAbortBookingPolling,
    );

    return () => {
      eventEmitter?.off(
        GlobalAppEventEnum.INIT_BOOKINGS_POLLING,
        onInitBookingPolling,
      );
      eventEmitter?.off(
        GlobalAppEventEnum.INIT_BOOKINGS_DATE_TIME_UPDATE_POLLING,
        onInitBookingsDateTimeUpdatePolling,
      );
      eventEmitter?.off(
        GlobalAppEventEnum.ABORT_BOOKINGS_POLLING,
        onAbortBookingPolling,
      );
    };
  }, [
    eventEmitter,
    onInitBookingPolling,
    onInitBookingsDateTimeUpdatePolling,
    onAbortBookingPolling,
  ]);

  useEffect(() => {
    return () => {
      if (subscriptionRef) {
        if (!subscriptionRef.current?.closed) {
          Log.debug(`Unsubscribing on component unmount`);
          subscriptionRef.current?.unsubscribe();
        }

        subscriptionRef.current = null;
      }
    };
  }, [subscriptionRef]);

  return {
    apiCheckBookingsPollingState,
    initBookingsPolling,
  };
};
